import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import EntityCardList from '../controls/EntityCardList';
import LocationMapperStateCounty from '../controls/LocationMapperStateCounty';
import NavRow from '../fields/NavRow';
import SelectField from '../fields/SelectField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { AreasOfLawValues, ExclusionRow, ExclusionsValues, LicensesValues } from '../types';
import { zodResolver } from '../validation';

const rowSchema = z.object({
  radio1: z.literal('Exception'),
  areaOfLaw: z.string().min(1, 'Area of Law is required'),
  stateAndCounty: z.object({
    state: z.string().min(1, 'State is required'),
    counties: z.array(
      z.object({
        title: z.string(),
        value: z.string(),
        countryCode: z.string(),
      }),
    ),
  }),
});

const schema = z.object({
  areaOfLawExclusions: z.array(rowSchema),
});

const DEFAULTS: ExclusionsValues = { areaOfLawExclusions: [] };

const makeEmptyRow = (): ExclusionRow => ({
  radio1: 'Exception',
  areaOfLaw: '',
  stateAndCounty: { state: '', counties: [] },
});

const ExclusionsForm = ({
  initialValues,
  allValues,
  onComplete,
  onBack,
  onCancelEdit,
  submitLabel,
}: TaskStepRenderArgs) => {
  const defaults = (initialValues as ExclusionsValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid },
  } = useForm<ExclusionsValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const areasOptions = useMemo(() => {
    const selectedAreas = ((allValues.task6 as AreasOfLawValues | undefined)?.areasOfLaw ?? []).flatMap(
      (c) => c.choices,
    );
    return selectedAreas.map((title) => ({ title, value: title }));
  }, [allValues.task6]);

  const availableStateCodes = useMemo(() => {
    const licenses = (allValues.task5 as LicensesValues | undefined)?.licenses ?? [];
    const set = new Set<string>();
    licenses.forEach((row) => row.countryAndState?.states?.forEach((s) => set.add(s.value)));
    return [...set].sort();
  }, [allValues.task5]);

  const onSubmit = handleSubmit((values) => onComplete(values));

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()} style={{ marginTop: '1rem' }}>
      <EntityCardList<ExclusionsValues, ExclusionRow>
        control={control}
        name="areaOfLawExclusions"
        entityLabel="Exclusion"
        trigger={trigger}
        makeEmptyRow={makeEmptyRow}
        isRowValid={(row) => rowSchema.safeParse(row).success}
        renderers={{
          getPrimary: (row) => row.areaOfLaw || '',
          getSecondary: (row) => row.stateAndCounty?.state || null,
          getTertiary: (row) =>
            row.stateAndCounty?.counties?.length ? row.stateAndCounty.counties.map((c) => c.title).join(', ') : null,
          secondaryIcon: 'object_location',
          tertiaryIcon: 'nav_map',
        }}
        renderEditForm={(index) => (
          <>
            <SelectField
              control={control}
              name={`areaOfLawExclusions.${index}.areaOfLaw`}
              label="Area of Law"
              required
              options={areasOptions}
            />
            <LocationMapperStateCounty
              control={control}
              name={`areaOfLawExclusions.${index}.stateAndCounty`}
              availableStateCodes={availableStateCodes}
              required
            />
          </>
        )}
      />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const Exclusions: TaskDefinition = {
  key: 'task7',
  label: 'Exclusions',
  title: 'Are there any exclusions to where you practice?',
  description:
    'For each previously selected area of law, LegalShield is assuming you practice in every county for the states that you are licensed in.\nIf you do not practice a particular area of law in all counties, please provide those below.',
  render: (args) => <ExclusionsForm {...args} />,
};

export default Exclusions;
